import React, { memo, useMemo } from 'react'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { Security } from '../../proto/generated/security'
import { OverviewWidgetResponse_Field, OverviewWidgetResponse_FieldGroup } from '../../proto/generated/overview-widget'
import OverviewValue from '../screener/OverviewValue'
import IndustryClassificationLink from '../industries/IndustryClassificationLink'
import { isClassificationFieldCode } from '../../util/industryNavigation'
import { makeStyles } from 'tss-react/mui'
import ScreenerOverviewRangeBlock from './ScreenerOverviewRangeBlock'
import OverviewPerformanceSection from './OverviewPerformanceSection'
import ShareholdingSection from './ShareholdingSection'
import OverviewSectionHeader from './OverviewSectionHeader'
import { OverviewSection, OVERVIEW_SECTION_ORDER, slugifyOverviewSection } from './OverviewSectionNav'
import { baseFieldCode, indexFieldsByBaseCode } from '../../util/overviewFields'

const useStyles = makeStyles()((theme) => ({
    gridContainer: {
        padding: theme.spacing(2)
    },
    fieldName: {
        color: theme.palette.text.secondary,
        fontSize: '10px',
        textTransform: 'uppercase'
    },
    peerComparison: {
        color: theme.palette.text.secondary,
        fontSize: '10px',
        lineHeight: 1.3
    }
}))

/** Peer context shown under a primary field instead of as its own cell. */
const PEER_COMPARISON_BY_FIELD_CODE: Record<string, { code: string; label: string }> = {
    symbolPE: { code: 'industryMedianPe', label: 'Ind. median' }
}

const PEER_COMPARISON_CODES = new Set(
    Object.values(PEER_COMPARISON_BY_FIELD_CODE).map((comparison) => comparison.code)
)

const formatPeerValue = (field: OverviewWidgetResponse_Field): string | null => {
    const value = Number(field.value)
    if (!field.value || !Number.isFinite(value) || value === 0) return null
    return value.toFixed(field.screenerField?.precision ?? 2)
}

const FieldGroupGrid = ({
    fields,
    securityForContent
}: {
    fields: OverviewWidgetResponse_Field[]
    securityForContent: Security | undefined
}) => {
    const { classes } = useStyles()

    const valueByCode = useMemo(() => indexFieldsByBaseCode(fields), [fields])

    const visibleFields = useMemo(
        () => fields.filter((field) => !PEER_COMPARISON_CODES.has(baseFieldCode(field.screenerField?.code))),
        [fields]
    )

    return (
        <Grid container rowSpacing={1.75} className={classes.gridContainer}>
            {visibleFields.map((field: OverviewWidgetResponse_Field) => {
                const comparison = PEER_COMPARISON_BY_FIELD_CODE[baseFieldCode(field.screenerField?.code)]
                const comparisonField = comparison && valueByCode.get(comparison.code)
                const comparisonValue = comparisonField ? formatPeerValue(comparisonField) : null
                const fieldCode = field.screenerField?.code

                return (
                    <Grid item xs={6} key={field.name}>
                        <Stack>
                            <Typography variant="caption" className={classes.fieldName}>
                                {field.name}
                            </Typography>
                            {securityForContent?.code && fieldCode && isClassificationFieldCode(fieldCode) ? (
                                <IndustryClassificationLink
                                    securityCode={securityForContent.code}
                                    fieldCode={fieldCode}
                                    fieldData={field.screenerField}
                                    value={field.value}
                                    overview
                                    isSecurityOverview={true}
                                />
                            ) : (
                                <OverviewValue overview={true} fieldData={field.screenerField} text={field.value} />
                            )}
                            {comparisonValue && (
                                <Typography variant="caption" className={classes.peerComparison}>
                                    {comparison.label} {comparisonValue}
                                </Typography>
                            )}
                        </Stack>
                    </Grid>
                )
            })}
        </Grid>
    )
}

export type SecurityOverviewFieldGroupsProps = {
    fieldGroups: OverviewWidgetResponse_FieldGroup[]
    currentVisibleOverviewSecurity: Security | undefined
    isOnScreener: boolean
    screenerTargetKey: string | null
    overviewSecurityKey: string | undefined
    securityForContent: Security | undefined
}

const renderFieldGroupsAreEqual = (prev: SecurityOverviewFieldGroupsProps, next: SecurityOverviewFieldGroupsProps) =>
    prev.fieldGroups === next.fieldGroups &&
    prev.currentVisibleOverviewSecurity === next.currentVisibleOverviewSecurity &&
    prev.isOnScreener === next.isOnScreener &&
    prev.screenerTargetKey === next.screenerTargetKey &&
    prev.overviewSecurityKey === next.overviewSecurityKey &&
    prev.securityForContent === next.securityForContent

const SecurityOverviewFieldGroups = ({
    fieldGroups,
    currentVisibleOverviewSecurity,
    isOnScreener,
    screenerTargetKey,
    overviewSecurityKey,
    securityForContent
}: SecurityOverviewFieldGroupsProps) => {
    // Skip empty groups; Performance tiles come from a separate response field.
    const renderableGroups = useMemo(
        () =>
            fieldGroups.filter(
                (group) => group.fields.length > 0 || group.name.toLowerCase() === 'performance'
            ),
        [fieldGroups]
    )

    return (
        <>
            {renderableGroups.length > 0 &&
                renderableGroups.map((fieldGroup: OverviewWidgetResponse_FieldGroup, index) => (
                    <OverviewSection
                        key={fieldGroup.name}
                        id={slugifyOverviewSection(fieldGroup.name)}
                        label={fieldGroup.name}
                        order={OVERVIEW_SECTION_ORDER.fieldGroupStart + index}
                    >
                    <Box boxShadow={0}>
                        <OverviewSectionHeader title={fieldGroup.name} />

                        {fieldGroup.name.toLowerCase() === 'performance' ? (
                            currentVisibleOverviewSecurity &&
                            (isOnScreener ? (
                                <ScreenerOverviewRangeBlock
                                    securityForContent={securityForContent}
                                    displaySecurity={currentVisibleOverviewSecurity}
                                    screenerTargetKey={screenerTargetKey}
                                    overviewSecurityKey={overviewSecurityKey}
                                    fieldGroups={fieldGroups}
                                />
                            ) : (
                                <OverviewPerformanceSection
                                    security={currentVisibleOverviewSecurity}
                                    fieldGroups={fieldGroups}
                                />
                            ))
                        ) : fieldGroup.name.toLowerCase() === 'shareholding' ? (
                            <ShareholdingSection fields={fieldGroup.fields} security={securityForContent} />
                        ) : (
                            <FieldGroupGrid fields={fieldGroup.fields} securityForContent={securityForContent} />
                        )}
                    </Box>
                    </OverviewSection>
                ))}
        </>
    )
}

export default memo(SecurityOverviewFieldGroups, renderFieldGroupsAreEqual)
